"use client";

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Alert, AlertDescription } from '@/app/components/ui/alert';
import { Badge } from '@/app/components/ui/badge';
import { 
  Download, 
  Loader2, 
  AlertTriangle, 
  CheckCircle,
  ExternalLink,
  Clock,
  Eye,
  ThumbsUp,
  User,
  Music
} from 'lucide-react';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';

interface TikTokDownloaderProps {
  className?: string;
}

export function TikTokDownloader({ className }: TikTokDownloaderProps) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [downloadInfo, setDownloadInfo] = useState<any>(null);

  const tiktokMutation = trpc.download.getTikTokRapidAPI.useMutation();

  const handleGetInfo = async () => {
    if (!url.trim()) {
      setError('Please enter a TikTok URL');
      return;
    }

    if (!url.includes('tiktok.com')) {
      setError('Please enter a valid TikTok URL');
      return;
    }

    setLoading(true);
    setError('');
    setDownloadInfo(null);

    try {
      const result = await tiktokMutation.mutateAsync({ url });
      setDownloadInfo(result);
      toast.success('TikTok video info retrieved successfully!');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get TikTok video info';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (downloadInfo?.downloadUrl) {
      const a = document.createElement('a');
      a.href = downloadInfo.downloadUrl;
      a.download = downloadInfo.filename;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('Download started!');
    }
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatViewCount = (count: number): string => {
    if (count >= 1000000) {
      return (count / 1000000).toFixed(1) + 'M';
    } else if (count >= 1000) {
      return (count / 1000).toFixed(1) + 'K';
    }
    return count.toString();
  };

  return (
    <div className={className}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Music className="h-5 w-5 text-black" />
            TikTok Downloader
          </CardTitle>
          <CardDescription>
            Download TikTok videos with audio. Works with public videos.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* URL Input */}
          <div className="space-y-2">
            <Label htmlFor="tiktok-url">TikTok URL</Label>
            <Input
              id="tiktok-url"
              type="url"
              placeholder="https://www.tiktok.com/@username/video/..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={loading}
            />
          </div>

          {/* Error Display */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleGetInfo}
              disabled={loading || !url.trim()}
              variant="outline"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Get Info
            </Button>

            {downloadInfo && (
              <Button
                onClick={handleDownload}
                disabled={loading}
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            )}
          </div>

          {/* Download Info Display */}
          {downloadInfo && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{downloadInfo.title || 'TikTok Video'}</CardTitle>
                <CardDescription className="flex items-center gap-4">
                  {downloadInfo.uploader && (
                    <span className="flex items-center gap-1">
                      <User className="h-4 w-4" />
                      {downloadInfo.uploader}
                    </span>
                  )}
                  {downloadInfo.duration && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {formatDuration(downloadInfo.duration)}
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Video Stats */}
                {(downloadInfo.view_count || downloadInfo.like_count) && (
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    {downloadInfo.view_count && (
                      <span className="flex items-center gap-1">
                        <Eye className="h-4 w-4" />
                        {formatViewCount(downloadInfo.view_count)} views
                      </span>
                    )}
                    {downloadInfo.like_count && (
                      <span className="flex items-center gap-1">
                        <ThumbsUp className="h-4 w-4" />
                        {formatViewCount(downloadInfo.like_count)} likes
                      </span>
                    )}
                  </div>
                )}

                {/* Download Info */}
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary">
                    {downloadInfo.filename}
                  </Badge>
                  {downloadInfo.platform && (
                    <Badge variant="outline">
                      {downloadInfo.platform}
                    </Badge>
                  )}
                </div>

                {/* Download Actions */}
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button
                    onClick={handleDownload}
                    disabled={loading}
                    size="sm"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                  <Button
                    onClick={() => window.open(downloadInfo.downloadUrl, '_blank')}
                    disabled={loading}
                    variant="outline"
                    size="sm"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Link
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Disclaimer */}
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Disclaimer:</strong> This tool is for educational purposes only. 
              Please respect TikTok's Terms of Service and only download content you have permission to download.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
}
